import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import {
  DEFAULT_QUEST_PREFERENCES,
  QUEST_COMPLETED_OBJECTIVE_DISPLAY,
  QUEST_COMPLETED_OBJECTIVE_DISPLAY_LABELS,
  QUEST_JOURNAL_DETAIL_SCROLL_ANCHOR_LABELS,
  QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS,
  QUEST_PROFILE_PORTRAIT_MODE_LABELS,
  QUEST_PROFILE_PORTRAIT_MODES,
  sanitizeQuestPreferences,
} from "../constants/questPreferences";
import "./DeleteWordModal.css";
import "./RegisterSettingsModal.css";
import "./EntrainementVoc.css";

function RadioChip({ name, label, checked, onChange, disabled = false }) {
  return (
    <label
      className={`entrainement-chip entrainement-chip--round${
        checked ? " entrainement-chip--checked" : ""
      }${disabled ? " entrainement-chip--disabled" : ""}`}
    >
      <input
        type="radio"
        className="entrainement-chip__input"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

export default function QuestSettingsModal({
  open,
  preferences = DEFAULT_QUEST_PREFERENCES,
  onPreferencesChange = () => {},
  onClose = () => {},
}) {
  const [draftPreferences, setDraftPreferences] = useState(DEFAULT_QUEST_PREFERENCES);

  useEffect(() => {
    if (!open) return;
    setDraftPreferences(sanitizeQuestPreferences(preferences));
  }, [open, preferences]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleApply = () => {
    onPreferencesChange?.(sanitizeQuestPreferences(draftPreferences));
    onClose();
  };

  const handleReset = () => {
    onPreferencesChange?.({ ...DEFAULT_QUEST_PREFERENCES });
    onClose();
  };

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="delete-word-modal register-settings-modal quest-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quest-settings-title"
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h2
          id="quest-settings-title"
          className="delete-word-modal__title delete-word-modal__title--gothic"
        >
          Paramètres des quêtes
        </h2>

        <div className="register-settings-modal__body">
          <section className="register-settings-modal__section">
            <h3 className="register-settings-modal__section-title">Préférence de scroll</h3>
            <p className="register-settings-modal__help">
              Positionnement automatique du détail de quête lorsque vous parcourez le journal.
            </p>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--3"
              role="radiogroup"
              aria-label="Préférence de scroll du journal de quêtes"
            >
              {Object.values(QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS).map((anchor) => (
                <RadioChip
                  key={anchor}
                  name="quest-journal-detail-scroll-anchor"
                  label={QUEST_JOURNAL_DETAIL_SCROLL_ANCHOR_LABELS[anchor]}
                  checked={draftPreferences.journalDetailScrollAnchor === anchor}
                  onChange={() =>
                    setDraftPreferences((prev) => ({
                      ...prev,
                      journalDetailScrollAnchor: anchor,
                    }))
                  }
                />
              ))}
            </div>
          </section>

          <section className="register-settings-modal__section">
            <h3 className="register-settings-modal__section-title">
              Affichage des objectifs accomplis
            </h3>
            <p className="register-settings-modal__help">
              Indique visuellement dans le détail de quête qu&apos;un objectif est atteint.
            </p>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
              role="radiogroup"
              aria-label="Affichage des objectifs accomplis"
            >
              {Object.values(QUEST_COMPLETED_OBJECTIVE_DISPLAY).map((display) => (
                <RadioChip
                  key={display}
                  name="quest-completed-objective-display"
                  label={QUEST_COMPLETED_OBJECTIVE_DISPLAY_LABELS[display]}
                  checked={draftPreferences.completedObjectiveDisplay === display}
                  onChange={() =>
                    setDraftPreferences((prev) => ({
                      ...prev,
                      completedObjectiveDisplay: display,
                    }))
                  }
                />
              ))}
            </div>
          </section>

          <section className="register-settings-modal__section">
            <h3 className="register-settings-modal__section-title">
              Portrait
            </h3>
            <p className="register-settings-modal__help">
              Choisissez ce qui s&apos;affiche à la place de votre photo dans l&apos;écran
              Avancement des titres. En mode blason, cochez un grade acquis pour sélectionner le
              blason à afficher.
            </p>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
              role="radiogroup"
              aria-label="Portrait"
            >
              {Object.values(QUEST_PROFILE_PORTRAIT_MODES).map((mode) => (
                <RadioChip
                  key={mode}
                  name="quest-profile-portrait-mode"
                  label={QUEST_PROFILE_PORTRAIT_MODE_LABELS[mode]}
                  checked={draftPreferences.profilePortraitMode === mode}
                  onChange={() =>
                    setDraftPreferences((prev) => ({
                      ...prev,
                      profilePortraitMode: mode,
                    }))
                  }
                />
              ))}
            </div>
          </section>
        </div>

        <div className="delete-word-modal__actions register-settings-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={handleReset}
          >
            Réinitialiser
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--confirm"
            onClick={handleApply}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
